import {
  getFilledPollOptions,
  hasMinimumPollOptions,
  validatePollOptionsForSubmit,
} from "./pollAttachmentRules";
import { postTypeSupportsExternalLink } from "./postTypeConfig";

/** Client-side post body limits for publish. Keep in sync with mobile/src/utils/postComposerUtils.js */
export const POST_CONTENT_MIN_LENGTH = 10;
export const POST_CONTENT_MAX_LENGTH = 2000;

export function isSafeExternalUrl(rawUrl) {
  const value = String(rawUrl ?? "").trim();
  if (!value) return true;
  try {
    const parsed = new URL(value);
    return (parsed.protocol === "http:" || parsed.protocol === "https:") && Boolean(parsed.hostname);
  } catch {
    return false;
  }
}

/**
 * @returns {string|null} Error message, or null when content is valid.
 */
export function validatePostContentForSubmit(content, { isDraft = false } = {}) {
  const length = String(content ?? "").trim().length;

  if (!length) {
    return isDraft
      ? "Please add some content to save as draft."
      : "Please share your story, idea, or experience.";
  }
  if (!isDraft && length < POST_CONTENT_MIN_LENGTH) {
    return `Please share a bit more (at least ${POST_CONTENT_MIN_LENGTH} characters). You've written ${length}.`;
  }
  if (length > POST_CONTENT_MAX_LENGTH) {
    return `Your post is a bit long. Please keep it under ${POST_CONTENT_MAX_LENGTH} characters.`;
  }
  return null;
}

/**
 * Validate composer state once and return errors keyed for the UI.
 */
export function validatePostComposer({
  content,
  isDraft = false,
  topics,
  attachments,
  actionType,
  pollAttachmentActive,
  pollOptions,
  billId,
  eventId,
  externalLinkUrl,
}) {
  const errors = {};
  const contentMessage = validatePostContentForSubmit(content, { isDraft });
  if (contentMessage) errors.content = contentMessage;

  if (!isDraft && (topics?.length ?? 0) > 3) {
    errors.topics = "You can select up to 3 topics.";
  }
  if (attachments.actionType && !actionType) {
    errors.actionType = "Please select what you did.";
  }
  if (pollAttachmentActive) {
    const pollResult = validatePollOptionsForSubmit(pollOptions);
    if (!pollResult.ok) errors.general = pollResult.error;
  }
  if (attachments.bill && billId == null) {
    errors.bill = "Please select a bill or remove the Bill attachment.";
  }
  if (attachments.event && eventId == null) {
    errors.event = "Please select an event or remove the Event attachment.";
  }
  if (externalLinkUrl && !isSafeExternalUrl(externalLinkUrl)) {
    errors.externalLink = "Enter a valid link beginning with http:// or https://.";
  }

  return {
    ok: Object.keys(errors).length === 0,
    errors,
  };
}

const EMPTY_LOCATION = {
  location_name: "",
  location_city: "",
  location_state: "",
  location_address: "",
};

/**
 * Clear form state when an attachment chip is toggled off.
 * Caller must still update `attachments[id]` separately.
 */
export function clearAttachmentOnToggle(attachmentId, helpers) {
  const {
    setPollOptions,
    setAllowsMultipleChoices,
    setLocationFields,
    setSelectedPlace,
    setActionType,
    setActionTypeError,
    setSelectedBill,
    setSelectedEvent,
    setContext,
    setImageUrl,
    setExternalLinkUrl,
    setExternalLinkTitle,
    createEmptyPollOptions,
  } = helpers;

  switch (attachmentId) {
    case "poll":
      setPollOptions(createEmptyPollOptions());
      setAllowsMultipleChoices(false);
      break;
    case "location":
      setLocationFields({ ...EMPTY_LOCATION });
      setSelectedPlace?.(null);
      break;
    case "actionType":
      setActionType("");
      setActionTypeError?.(null);
      break;
    case "bill":
      setSelectedBill(null);
      setContext?.((c) => (c?.type === "bill" ? null : c));
      break;
    case "event":
      setSelectedEvent(null);
      setContext?.((c) => (c?.type === "event" ? null : c));
      break;
    case "image":
      setImageUrl("");
      break;
    case "externalLink":
      setExternalLinkUrl("");
      setExternalLinkTitle("");
      break;
    default:
      break;
  }
}

/**
 * Build API payload for create/update post. Pure — no side effects.
 * Pass `anonymous` as undefined once the post is published (the backend locks it).
 */
export function buildPostWritePayload({
  anonymous,
  status,
  isEditMode,
  editSnapshot,
  attachments,
  pollAttachmentActive,
  supportsGeoTarget = true,
  selectedPostType,
  content,
  topics,
  visibility,
  geoTarget,
  imageUrl,
  actionType,
  pollOptions,
  allowsMultipleChoices,
  locationFields,
  billId,
  eventId,
  externalLinkUrl,
  externalLinkTitle,
}) {
  const filledPoll = getFilledPollOptions(pollOptions);

  const data = {
    post_type: selectedPostType || "idea",
    content: content.trim(),
    topics: topics || [],
    status,
    visibility,
  };

  if (anonymous !== undefined) {
    data.anonymous = anonymous;
  }

  if (supportsGeoTarget) {
    data.geo_target = geoTarget;
  }

  if (attachments.image || imageUrl) {
    data.image_url = imageUrl || null;
  } else if (isEditMode && editSnapshot.hadImage) {
    data.image_url = null;
  } else {
    data.image_url = imageUrl || null;
  }

  if (attachments.actionType) {
    data.action_type = actionType || null;
  } else if (isEditMode && editSnapshot.hadActionType) {
    data.action_type = null;
  }

  // Once a poll has votes, the backend rejects any poll mutation. Omitting
  // both fields allows unrelated content/audience edits to proceed safely.
  if (!(isEditMode && editSnapshot.pollHasVotes)) {
    if (pollAttachmentActive && hasMinimumPollOptions(pollOptions)) {
      data.options = filledPoll;
      data.allows_multiple_choices = allowsMultipleChoices;
    } else if (isEditMode && editSnapshot.hadPoll && !pollAttachmentActive) {
      data.options = [];
    }
  }

  if (attachments.location) {
    data.location_name = locationFields.location_name || "";
    data.location_city = locationFields.location_city || "";
    data.location_state = locationFields.location_state || "";
    data.location_address = locationFields.location_address || "";
  } else if (isEditMode && editSnapshot.hadLocation) {
    data.location_name = "";
    data.location_city = "";
    data.location_state = "";
    data.location_address = "";
  }

  if (attachments.bill) {
    data.bill_id = billId ?? null;
  } else if (isEditMode && editSnapshot.hadBill) {
    data.bill_id = null;
  } else if (billId != null) {
    data.bill_id = billId;
  }

  if (attachments.event) {
    data.event_id = eventId ?? null;
  } else if (isEditMode && editSnapshot.hadEvent) {
    data.event_id = null;
  } else if (eventId != null) {
    data.event_id = eventId;
  }

  if (
    (attachments.externalLink || postTypeSupportsExternalLink(selectedPostType)) &&
    externalLinkUrl
  ) {
    data.external_link_url = externalLinkUrl;
    data.external_link_title = externalLinkTitle || null;
  } else if (isEditMode && editSnapshot.hadExternalLink) {
    data.external_link_url = null;
    data.external_link_title = null;
  }

  return data;
}
